import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { debounce } from 'lodash';
import Tippy from '@tippyjs/react';
import { toast } from 'react-toastify';
import { Card, Spinner } from '@opendate-app/setlist';
import AsyncSelect from 'react-select/async';
import client from '../../../shared/api/client';
import darkSelectStyles from '../../../shared/darkSelectStyles';

const axios = require('axios').default;

const debouncedFetch = debounce((inputValue, callback) => {
  axios.get('/artists/search', { params: { name: inputValue } })
    .then((response) => {
      callback(response.data.map((artist) => ({ value: artist.id, label: artist.name })));
    });
}, 250);

export default function PotentialArtistFans({ team, selectedArtist }) {
  const [artist, setArtist] = useState(selectedArtist || null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const latestArtistId = useRef(null);

  const fetchPotentialFans = (artistId) => {
    latestArtistId.current = artistId;
    setLoading(true);
    setData(null);
    client.get(`/teams/${team.id}/fans/potential_artist_fans.json`, { params: { artist_id: artistId } })
      .then((res) => {
        if (latestArtistId.current !== artistId) return;
        setData(res.data);
      })
      .catch(() => {
        if (latestArtistId.current !== artistId) return;
        setData(null);
        toast.error("Couldn't load potential fans.");
      })
      .finally(() => {
        if (latestArtistId.current === artistId) setLoading(false);
      });
  };

  const onArtistChange = (option) => {
    setArtist(option);
    fetchPotentialFans(option.value);
  };

  useEffect(() => {
    if (selectedArtist) fetchPotentialFans(selectedArtist.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card padding="md">
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Potential artist fans</h2>
        <Tippy content="Search an artist and Opendate will display the number of potential fans based on artist, similar artists, and genre.">
          <i className="fas fa-info-circle text-muted ml-2" style={{ fontSize: 16 }} />
        </Tippy>
      </div>

      <p style={{ fontSize: 14, color: 'var(--sl-text-quaternary, #717680)' }}>
        Search for an artist and Opendate will return all potential fans.
      </p>

      <AsyncSelect
        cacheOptions
        styles={darkSelectStyles}
        menuPortalTarget={document.querySelector('body')}
        defaultOptions
        onChange={onArtistChange}
        value={artist}
        loadOptions={(inputValue, callback) => debouncedFetch(inputValue, callback)}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <Spinner label="Loading…" />
        </div>
      ) : !data ? null : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, marginTop: 24, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 200 }}>
            {data.artist.image_thumb_url_for_json && (
              <img
                src={data.artist.image_thumb_url_for_json}
                alt={data.artist.name}
                style={{ height: 48, width: 48, borderRadius: 4 }}
              />
            )}
            <strong style={{ fontSize: 14 }}>{data.artist.name}</strong>
          </div>

          <div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{data.potential_fans.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: 'var(--sl-text-quaternary, #717680)' }}>POTENTIAL FANS</div>
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{data.artist_fans.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: 'var(--sl-text-quaternary, #717680)' }}>ARTIST FANS</div>
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{data.similar_artist_fans.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: 'var(--sl-text-quaternary, #717680)' }}>SIMILAR ARTIST FANS</div>
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{data.genre_fans.toLocaleString()}</div>
            <div style={{ fontSize: 10, color: 'var(--sl-text-quaternary, #717680)' }}>GENRE FANS</div>
          </div>

          <div style={{ flexBasis: '100%', borderTop: '1px solid var(--sl-border-secondary, #e8e8e8)', paddingTop: 12, textAlign: 'right' }}>
            <a href={`/teams/${team.id}/fans/potential_artist_fans.csv?artist_id=${data.artist.id}`}>
              View the list
              <i className="far fa-chevron-right ml-2" />
            </a>
          </div>
        </div>
      )}
    </Card>
  );
}

PotentialArtistFans.propTypes = {
  team: PropTypes.object.isRequired,
  selectedArtist: PropTypes.object,
};
